import i18n from '../i18n';

const loc = () => (i18n.language === 'ar' ? 'ar' : 'en-GB');

/** All times are shown in the school's time zone, wherever the viewer is */
let schoolTz: string | undefined;
export function setDisplayTimeZone(tz: string | undefined) {
  schoolTz = tz;
}

export function time(iso: string | null | undefined, timeZone = schoolTz) {
  if (!iso) return '';
  return new Intl.DateTimeFormat(loc(), { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone }).format(new Date(iso));
}

export function dateLabel(isoDate: string) {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(loc(), { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** A school day such as "Wed, 4 Nov" (for lists and tables) */
export function shortDate(isoDate: string) {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(loc(), { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** A calendar date such as 3 Dec 2026, in the current language */
export function date(iso: string | Date, timeZone = schoolTz) {
  return new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone, numberingSystem: 'latn' }).format(new Date(iso));
}

/** Money with the currency, e.g. $2,400 (same in Arabic: the Arabic locale would write "US$") */
export function money(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0, numberingSystem: 'latn' }).format(amount);
}

export function dateTime(iso: string, timeZone = schoolTz) {
  return new Intl.DateTimeFormat(loc(), { dateStyle: 'medium', timeStyle: 'short', hourCycle: 'h23', timeZone }).format(new Date(iso));
}

/** "Wed, 4 Nov • 21:47" in the school's time zone */
export function dayTime(iso: string, timeZone = schoolTz) {
  const day = new Intl.DateTimeFormat(loc(), { weekday: 'short', day: 'numeric', month: 'short', timeZone }).format(new Date(iso));
  return `${day} • ${time(iso, timeZone)}`;
}

export function minutesSince(iso: string | null) {
  if (!iso) return 0;
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
}

/** Arabic-Indic (٠-٩) and Eastern Arabic-Indic (۰-۹) digits → ASCII */
export const toAsciiDigits = (s: string) =>
  s.replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660)).replace(/[\u06f0-\u06f9]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

/** Today's date (YYYY-MM-DD) at the school, whatever the device's time zone */
export const schoolToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: schoolTz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

/** Common relationship words are translated; anything a family typed themselves is shown as typed */
export const relationLabel = (value: string) => {
  const key = value.trim().toLowerCase();
  return i18n.exists(`relation.${key}`) ? i18n.t(`relation.${key}`) : value;
};

/** Sort key for grades: pre-K and kindergarten first, then numeric grades (1, 2, … 10), then anything else */
export function gradeRank(grade: string): number {
  const g = grade.trim().toLowerCase();
  if (/^(pre-?k|pk|nursery|روضة ?1)/.test(g)) return -3;
  const kg = /^(kg|k|روضة)\s*-?\s*(\d)?$/.exec(g);
  if (kg) return -2 + (kg[2] ? Number(kg[2]) / 10 : 0);
  const n = parseFloat(g.replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 1000;
}

/** Student name in the current UI language */
export const nameOf = (s: { name: string; nameAr?: string | null }) => (i18n.language === 'ar' && s.nameAr ? s.nameAr : s.name);

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    // "الخطيب" → "خ", not "ا": the Arabic article isn't part of the name's sound
    .map((p) => (/^ال.{2,}/.test(p) ? p[2] : p[0]))
    .join('')
    .toUpperCase();

/** "dismissal.release" → "Released"; unknown actions are made readable instead of shown raw */
export function actionLabel(action: string) {
  const key = action.replace(/\./g, '_');
  const fallback = action.replace(/[._]/g, ' ');
  return i18n.t(`actions.${key}`, { defaultValue: fallback.charAt(0).toUpperCase() + fallback.slice(1) });
}

/** Whole numbers with separators in the current language, e.g. 1,000 */
export const num = (n: number) => new Intl.NumberFormat(i18n.language === 'ar' ? 'ar' : 'en-US', { numberingSystem: 'latn' }).format(n);
